import React, { useState } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Autocomplete,
  TextField,
} from "@mui/material";
import { peopleAPI } from "api/Community/PeopleAPI";
import { RectangularTextButton } from "components/Base/Buttons";

interface DiscipleshipAddPeopleButtonProps {
  nodeData: any;
  currentItems: any[];
  onAdded?: () => void;
  disabled?: boolean;
}

function extractStageId(nodeData: any): number | null {
  const candidates = [
    nodeData?.data?.stage_id,
    nodeData?.data?.stage?.id,

    // only as a last resort
    nodeData?.id,
  ];

  for (const value of candidates) {
    if (value !== undefined && value !== null && value !== "") {
      const parsed = Number(value);
      if (!Number.isNaN(parsed)) return parsed;
    }
  }

  return null;
}

export default function DiscipleshipAddPeopleButton({
  nodeData,
  currentItems,
  onAdded,
  disabled = false,
}: DiscipleshipAddPeopleButtonProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [allPeople, setAllPeople] = useState<any[]>([]);
  const [selectedPeople, setSelectedPeople] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [loadingPeople, setLoadingPeople] = useState(false);

  const openDialog = async () => {
    setDialogOpen(true);
    setLoadingPeople(true);

    try {
      const list = await peopleAPI.getAll();
      const existingIds = new Set(currentItems.map((p) => Number(p.id)));
      const available = list.filter((p) => !existingIds.has(Number(p.id)));
      setAllPeople(available);
    } catch (error) {
      console.error("Failed to load people for add dialog:", error);
      setAllPeople([]);
    } finally {
      setLoadingPeople(false);
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedPeople([]);
  };

  const handleAdd = async () => {
    console.log("DISCIPLESHIP ADD DEBUG");
    console.log("nodeData:", nodeData);
    console.log("nodeData.data:", nodeData?.data);
    console.log("selectedPeople:", selectedPeople);

    const stageId = extractStageId(nodeData);

    console.log("resolved stageId:", stageId);
    console.log("stage candidates", {
      nodeId: nodeData?.id,
      dataId: nodeData?.data?.id,
      dataStageId: nodeData?.data?.discipleship_stage_id,
      dataStageObjId: nodeData?.data?.discipleship_stage?.id,
      nodeStageId: nodeData?.discipleship_stage_id,
      nodeStageObjId: nodeData?.discipleship_stage?.id,
    });

    if (!stageId) {
      console.error("No discipleship stage id could be resolved from nodeData.");
      return;
    }

    if (selectedPeople.length === 0) {
      console.error("No people selected to add.");
      return;
    }

    setSaving(true);

    try {
      const results = await Promise.allSettled(
        selectedPeople.map((person) =>
          peopleAPI.updateField(
            Number(person.id),
            "discipleship_stage_id",
            stageId
          )
        )
      );

      console.log("Add people results:", results);

      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          console.log(`Updated person ${index}:`, result.value);
        } else {
          console.error(`Failed person ${index}:`, result.reason);
        }
      });

      closeDialog();
      await onAdded?.();
    } catch (error) {
      console.error("Failed to add people to discipleship stage:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <RectangularTextButton
        onClick={openDialog}
        disabled={disabled}
      >
        Add People
      </RectangularTextButton>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Select People to Add</DialogTitle>

        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Autocomplete
              multiple
              options={allPeople}
              loading={loadingPeople}
              value={selectedPeople}
              onChange={(_, value) => setSelectedPeople(value)}
              getOptionLabel={(p) =>
                `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim()
              }
              isOptionEqualToValue={(option, value) =>
                Number(option.id) === Number(value.id)
              }
              renderInput={(params) => (
                <TextField {...params} label="Search people…" />
              )}
            />
          </Box>

          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
            <RectangularTextButton
              colour="highlighted"
              onClick={handleAdd}
              disabled={selectedPeople.length === 0 || saving}
            >
              Add Selected People
            </RectangularTextButton>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}